import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '../context/TranslationsContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag, CreditCard, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getLocalizedPath } from '../utils/routes';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_KEYS_FULL = ['january','february','march','april','may_full','june','july','august','september','october','november','december'];
const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

const EventDetailPage = () => {
  const { i18n } = useTranslation();
  const { td } = useTranslations();
  const { slug } = useParams();
  const lang = i18n.language;

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchEvent(); }, [slug]);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/events/${slug}`);
      setEvent(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'not_found' : 'error');
    } finally {
      setLoading(false);
    }
  };

  const ls = (field, fallback = '') => {
    if (!field) return fallback;
    if (typeof field === 'string') return field;
    return field[lang] || field.pl || field.en || Object.values(field)[0] || fallback;
  };

  const formatDateFull = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const monthKey = MONTH_KEYS_FULL[d.getMonth()];
    const month = td(`months.${monthKey}`) || d.toLocaleDateString('pl-PL', { month: 'long' });
    const dayKey = DAY_KEYS[d.getDay()];
    const dayName = td(`days.${dayKey}`) || d.toLocaleDateString('pl-PL', { weekday: 'long' });
    return `${dayName}, ${day} ${month} ${d.getFullYear()}`;
  };

  const formatDayOfWeek = (dayIndex) => {
    const key = DAY_KEYS[dayIndex];
    return td(`days.${key}`) || key;
  };

  const getBadgeLabel = (badge) => {
    if (typeof badge === 'string') return badge;
    return badge[lang] || badge.pl || badge.en || Object.values(badge)[0] || '';
  };

  const eventsPath = getLocalizedPath('events');
  const bookPath = getLocalizedPath('book');

  const [fitnetTranslations, setFitnetTranslations] = useState({});
  
  useEffect(() => {
    axios.get(`${API}/api/admin/fitnet-translations`).then(res => setFitnetTranslations(res.data)).catch(() => {});
  }, []);

  const normalizeNameToKey = (name) => {
    if (!name) return '';
    return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/ł/g, 'l').replace(/Ł/g, 'L')
      .replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  };

  const makeSlug = (name) => name.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const getBookLink = () => {
    if (!event?.badges || event.badges.length === 0) return bookPath;
    // Get unique badge labels (uppercase)
    const labels = event.badges.map(b => (typeof b === 'string' ? b : b.pl || b.en || '').toUpperCase());
    const unique = [...new Set(labels)];
    if (unique.length !== 1) return bookPath;
    
    // Generate translated slug from fitnet translations
    const key = normalizeNameToKey(unique[0]);
    const translated = fitnetTranslations[key]?.[lang];
    if (translated) return `${bookPath}/${makeSlug(translated)}`;
    return `${bookPath}/${makeSlug(unique[0])}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen page-gradient pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="animate-pulse">
            <div className="h-8 bg-white/10 rounded w-1/2 mb-4" />
            <div className="aspect-square max-w-md bg-white/10 rounded-xl mb-6" />
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => <div key={i} className="h-4 bg-white/10 rounded w-3/4" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen page-gradient pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h1 className="text-3xl font-bold text-white mb-4">
            {error === 'not_found' ? (td('events.noEvents') || 'Wydarzenie nie znalezione') : 'Error'}
          </h1>
          <Link to={eventsPath}>
            <Button><ArrowLeft className="w-4 h-4 mr-2" /> {td('events.backToEvents') || 'Wróć do wydarzeń'}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen page-gradient pt-28 pb-16" data-testid="event-detail-page">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Back */}
        <Link to={eventsPath} className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {td('events.backToEvents') || 'Wróć do wydarzeń'}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid md:grid-cols-[400px_1fr] gap-8"
        >
          {/* Image */}
          <div className="relative aspect-square rounded-2xl overflow-hidden bg-white/5">
            {event.image ? (
              <img
                src={event.image.startsWith('/') ? `${API}${event.image}` : event.image}
                alt={ls(event.title)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-purple-600/20">
                <Calendar className="w-20 h-20 text-white/20" />
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {event.badges?.map((badge, i) => (
                <span key={i} className="rounded-full border border-primary/30 bg-primary/20 px-3 py-1 text-xs text-primary font-medium">
                  {getBadgeLabel(badge)}
                </span>
              ))}
              {event.isRecurring && (
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
                  {td('events.recurring') || 'Cykliczne'}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {ls(event.title)}
            </h1>

            {/* Details */}
            <div className="space-y-4 mb-8">
              {/* Date */}
              <div className="flex items-center gap-3 text-gray-300">
                <Calendar className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <span className="font-medium">{td('events.date') || 'Data'}: </span>
                  {event.isRecurring ? (
                    <span>
                      {(event.recurringConfig?.daysOfWeek || []).sort((a,b) => a-b).map(d => formatDayOfWeek(d)).join(', ')}
                      {event.recurringConfig?.endDate && (
                        <span className="text-gray-500 ml-1">
                          ({td('events.until') || 'do'} {formatDateFull(event.recurringConfig.endDate)})
                        </span>
                      )}
                    </span>
                  ) : (
                    <span>{formatDateFull(event.nextDate || event.date)}</span>
                  )}
                </div>
              </div>

              {/* Time */}
              {event.time && (
                <div className="flex items-center gap-3 text-gray-300">
                  <Clock className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>
                    <span className="font-medium">{td('events.time') || 'Godzina'}: </span>
                    {event.time}{event.endTime && ` - ${event.endTime}`}
                  </span>
                </div>
              )}

              {/* Price */}
              <div className="flex items-center gap-3 text-gray-300">
                <CreditCard className="w-5 h-5 text-primary flex-shrink-0" />
                <span>
                  <span className="font-medium">{td('events.price') || 'Cena'}: </span>
                  {event.price === 0 || event.price == null
                    ? (td('events.free') || 'Bezpłatne')
                    : `${event.price.toFixed(2)} zł`}
                </span>
              </div>
            </div>

            {/* Book button */}
            <Link to={getBookLink()}>
              <Button className="w-full sm:w-auto text-base px-8 py-3">
                {td('events.bookNow') || 'Rezerwuj'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Description */}
        {ls(event.description) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-12"
          >
            <div
              className="prose prose-invert prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: ls(event.description) }}
            />
          </motion.div>
        )}

        {/* Upcoming dates for recurring events */}
        {event.isRecurring && event.nextOccurrences?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-12"
          >
            <h2 className="text-xl font-bold text-white mb-6">
              {td('events.upcomingDates') || 'Najbliższe terminy'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {event.nextOccurrences.map((date, i) => {
                const d = new Date(date);
                return (
                  <div
                    key={i}
                    className="text-center py-4 px-3 rounded-xl border border-white/10 bg-white/5 hover:border-primary/30 transition-colors"
                  >
                    <div className="text-xs text-primary mb-1 capitalize">
                      {td(`days.${DAY_KEYS[d.getDay()]}`) || DAY_KEYS[d.getDay()]}
                    </div>
                    <div className="text-2xl font-bold text-white">{d.getDate()}</div>
                    <div className="text-xs text-gray-400">
                      {td(`months.${MONTH_KEYS_FULL[d.getMonth()]}`) || ''}
                    </div>
                    {event.time && (
                      <div className="text-xs text-gray-500 mt-1">{event.time}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EventDetailPage;
